import type { ReactNode } from 'react';
import { createColumnHelper, flexRender, getCoreRowModel, useReactTable } from '@tanstack/react-table';
import { ChevronDown, ChevronUp, ChevronsUpDown } from 'lucide-react';
import { EmptyState } from '@/components/ui/EmptyState';
import { Checkbox } from '@/components/ui/Checkbox';
import { Skeleton } from '@/components/ui/Skeleton';
import { COMMON_STRINGS } from '@/lib/constants/common-strings';

export interface DataTableColumn<Row> {
  /** Stable id; matches the server sort field name when sortable. */
  id: string;
  header: ReactNode;
  cell: (row: Row) => ReactNode;
  sortable?: boolean;
  /** Optional extra classes for this column's cells + header (alignment etc). */
  className?: string;
}

export interface DataTableProps<Row> {
  columns: DataTableColumn<Row>[];
  rows: Row[];
  /** Stable row key. */
  rowKey: (row: Row) => string;
  /** Shown when there are no rows (required per ui-guidelines "empty state"). */
  emptyState: { title: string; description?: string; icon?: ReactNode; action?: ReactNode };
  /** Accessible caption for the table. */
  caption: string;
  /** Renders skeleton placeholders instead of the table or empty state. */
  isLoading?: boolean;
  /** Current server sort string, e.g. "-email". Only meaningful with onSortChange. */
  sort?: string;
  onSortChange?: (field: string) => void;
  /** Supplying both together enables row selection (header + per-row checkboxes). */
  selectedIds?: string[];
  onSelectionChange?: (ids: string[]) => void;
  /** Optional trailing per-row action slot (e.g. a row actions menu). */
  rowActions?: (row: Row) => ReactNode;
  /**
   * Makes the whole row activate (e.g. open a detail panel). Cells that hold
   * their own controls stop propagation, so the checkbox and the actions menu
   * keep working. Keyboard users use the focusable control inside the row
   * instead - the row itself is deliberately not a tab stop, which would put a
   * second stop on every row for no gain.
   */
  onRowClick?: (row: Row) => void;
  /** Rows drawn in the selected state (tint plus a left accent bar). */
  activeRowId?: string | null;
  /**
   * Excludes a row from selection - its checkbox is disabled AND select-all skips
   * it. Both halves are required: gating only the checkbox lets "select all"
   * silently put the row back into the selection.
   */
  isRowSelectable?: (row: Row) => boolean;
  /** Footer region inside the card (e.g. Pagination), below the rows. */
  footer?: ReactNode;
  /** Header region inside the card (e.g. tabs and a filter toolbar), above the rows. */
  toolbar?: ReactNode;
  /**
   * Makes the card fill its parent's height and scroll the ROWS rather than the
   * page: toolbar and footer stay pinned, and the sticky header actually sticks.
   *
   * Off by default. `overflow-x-auto` alone makes an element a vertical scroll
   * container too (CSS will not leave overflow-y visible when overflow-x is not),
   * so without this the sticky header anchors to a container that never scrolls
   * and the whole page scrolls instead - which is also what left a screenful of
   * blank space under a short table. Requires every ancestor up to the scroll
   * root to be a flex column with `min-h-0`.
   */
  fillHeight?: boolean;
}

const TABLE_CLASS = 'text-text-primary';
const HEADER_CLASS = 'text-text-muted';
const ROW_BORDER_CLASS = 'border-border-subtle';

/**
 * Left inset for the first column when there is NO selection column.
 *
 * Every ordinary cell carries `pr-4` only; the table's left inset came for free
 * from the selection cell's `px-4`. A table without selection therefore had its
 * first column flush against the container edge, which is what made the Team
 * Access tables look unpadded. Applied only when `selectionEnabled` is false, or
 * the two would compound.
 */
const LEADING_CELL_INSET = 'pl-4';
const CARD_CLASS =
  'bg-surface-card border-border-strong shadow-[var(--card-shadow)] flex flex-col overflow-hidden rounded-card border';

/** One glyph per sort state, so direction is visible and not only in aria-sort. */
const SORT_ICON = {
  ascending: ChevronUp,
  descending: ChevronDown,
  none: ChevronsUpDown,
} as const;

/** Derive one header's aria-sort from the active sort string, once, not re-tested per header. */
function ariaSortFor(sort: string | undefined, id: string): 'ascending' | 'descending' | undefined {
  if (sort === id) return 'ascending';
  if (sort === `-${id}`) return 'descending';
  return undefined;
}

/**
 * Server-driven data table (mnr-standards `common/` composite). Domain-free so
 * every admin console table, plus the existing Team Members and Shared Profile
 * Recipients tables, reuse it.
 *
 * Built on @tanstack/react-table with ONLY getCoreRowModel wired. Sorting,
 * filtering, and paging are all server-driven elsewhere (useTableQuery + the
 * API), so getSortedRowModel/getFilteredRowModel/getPaginationRowModel are
 * deliberately never imported - wiring them would sort/filter/page just the
 * current page's rows client-side, silently producing wrong results that
 * look right in a quick manual check.
 *
 * Selection is a controlled prop, not tanstack's internal row-selection state,
 * so the console that owns this table can clear it on page/filter change.
 */
export function DataTable<Row>({
  columns,
  rows,
  rowKey,
  emptyState,
  caption,
  isLoading = false,
  sort,
  onSortChange,
  selectedIds,
  onSelectionChange,
  rowActions,
  onRowClick,
  activeRowId = null,
  isRowSelectable,
  footer,
  toolbar,
  fillHeight = false,
}: DataTableProps<Row>) {
  const selectionEnabled = selectedIds !== undefined && onSelectionChange !== undefined;

  const columnHelper = createColumnHelper<Row>();
  const tanstackColumns = columns.map((column) =>
    columnHelper.display({
      id: column.id,
      cell: (ctx) => column.cell(ctx.row.original),
    }),
  );

  const table = useReactTable({
    data: rows,
    columns: tanstackColumns,
    getCoreRowModel: getCoreRowModel(),
    getRowId: rowKey,
  });

  // Selectable ids only, so select-all and the all/some-selected states agree
  // with what the per-row checkboxes actually allow.
  const pageIds = rows.filter((row) => isRowSelectable?.(row) ?? true).map(rowKey);
  const allSelected = selectionEnabled && pageIds.length > 0 && pageIds.every((id) => selectedIds!.includes(id));
  const someSelected = selectionEnabled && !allSelected && pageIds.some((id) => selectedIds!.includes(id));

  function toggleHeaderCheckbox() {
    if (!onSelectionChange) return;
    onSelectionChange(allSelected ? [] : pageIds);
  }

  function toggleRowCheckbox(id: string) {
    if (!onSelectionChange || !selectedIds) return;
    onSelectionChange(
      selectedIds.includes(id) ? selectedIds.filter((existing) => existing !== id) : [...selectedIds, id],
    );
  }

  // Loading and empty states render inside the card too, so the console does
  // not visibly collapse to nothing between states.
  function body() {
    if (isLoading) {
      return (
        <div className={`flex flex-col gap-2 p-6 ${fillHeight ? 'min-h-0 flex-1' : ''}`.trim()}>
          <span className="sr-only" role="status" aria-live="polite">
            {COMMON_STRINGS.LOADING}
          </span>
          <Skeleton rows={5} className="h-12 w-full" />
        </div>
      );
    }

    if (rows.length === 0) {
      // Measured: without flex-1 the footer lands mid-card and leaves a few
      // hundred px of dead white space beneath it, inside the card's own border.
      return (
        <EmptyState
          fillHeight={fillHeight}
          icon={emptyState.icon}
          title={emptyState.title}
          description={emptyState.description}
          action={emptyState.action}
        />
      );
    }

    const bodyRows = table.getRowModel().rows;

    return (
      // `relative` so this container is the containing block for any
      // absolutely-positioned descendant (sr-only labels, captions). Otherwise
      // they escape to the initial containing block and add their full offset to
      // the document's scroll height, however clipped they look.
      <div className={`relative ${fillHeight ? 'min-h-0 flex-1 overflow-auto' : 'overflow-x-auto'}`}>
        <table className={`w-full border-collapse text-left text-sm ${TABLE_CLASS}`}>
          <caption className="sr-only">{caption}</caption>
          <thead>
            <tr className={`border-b ${ROW_BORDER_CLASS}`}>
              {selectionEnabled && (
                <th scope="col" className="bg-surface-page sticky top-0 z-10 w-10 px-4 py-3.5">
                  <Checkbox
                    label="Select all rows on this page"
                    hideLabel
                    checked={allSelected}
                    ref={(el) => {
                      if (el) el.indeterminate = someSelected;
                    }}
                    onChange={toggleHeaderCheckbox}
                  />
                </th>
              )}
              {columns.map((column, index) => {
                const sortState = column.sortable ? ariaSortFor(sort, column.id) : undefined;
                const leadingInset = !selectionEnabled && index === 0 ? LEADING_CELL_INSET : '';
                // One lookup, not a ternary chain re-testing the sort state per
                // glyph (mnr-standards no-repeated-ternary-on-one-discriminant).
                const SortIcon = SORT_ICON[sortState ?? 'none'];

                return (
                  <th
                    key={column.id}
                    scope="col"
                    aria-sort={sortState}
                    className={`bg-surface-page sticky top-0 z-10 pr-4 whitespace-nowrap py-3.5 font-sans text-[12px] font-semibold tracking-wide uppercase ${leadingInset} ${HEADER_CLASS} ${column.className ?? ''}`.trim()}
                  >
                    {column.sortable && onSortChange ? (
                      <button
                        type="button"
                        className="hover:text-text-primary focus-visible:ring-accent flex cursor-pointer items-center gap-1 transition-colors focus-visible:ring-2 focus-visible:outline-none"
                        onClick={() => onSortChange(column.id)}
                      >
                        {column.header}
                        <SortIcon size={14} aria-hidden />
                      </button>
                    ) : (
                      column.header
                    )}
                  </th>
                );
              })}
              {rowActions && <th scope="col" className="bg-surface-page sticky top-0 z-10 py-3.5" />}
            </tr>
          </thead>
          <tbody>
            {bodyRows.map((row) => {
              const id = row.id;
              const selectable = isRowSelectable?.(row.original) ?? true;
              const isSelected = selectionEnabled && selectedIds!.includes(id);
              const isActive = activeRowId === id;
              const highlighted = isSelected || isActive;

              return (
                <tr
                  key={id}
                  onClick={onRowClick ? () => onRowClick(row.original) : undefined}
                  aria-selected={highlighted || undefined}
                  className={`border-b transition-colors last:border-0 ${ROW_BORDER_CLASS} ${
                    highlighted ? 'bg-row-selected' : 'hover:bg-row-hover'
                  } ${onRowClick ? 'cursor-pointer' : ''}`.trim()}
                >
                  {selectionEnabled && (
                    <td
                      className={`border-l-[3px] px-4 py-4 align-middle ${highlighted ? 'border-accent' : 'border-transparent'}`}
                      // The checkbox must not also trigger the row's own click.
                      onClick={(event) => event.stopPropagation()}
                    >
                      <Checkbox
                        label={
                          selectable
                            ? `Select row ${id}`
                            : `Row ${id} cannot be selected`
                        }
                        hideLabel
                        checked={isSelected}
                        disabled={!selectable}
                        onChange={() => toggleRowCheckbox(id)}
                      />
                    </td>
                  )}
                  {row.getVisibleCells().map((cell, index) => {
                    const column = columns.find((c) => c.id === cell.column.id)!;
                    const leadingInset =
                      !selectionEnabled && index === 0 ? LEADING_CELL_INSET : '';
                    return (
                      <td
                        key={cell.id}
                        className={`py-4 pr-4 align-middle ${leadingInset} ${column.className ?? ''}`.trim()}
                      >
                        {flexRender(cell.column.columnDef.cell, cell.getContext())}
                      </td>
                    );
                  })}
                  {rowActions && (
                    <td
                      className="py-4 pr-4 align-middle"
                      onClick={(event) => event.stopPropagation()}
                    >
                      {rowActions(row.original)}
                    </td>
                  )}
                </tr>
              );
            })}
          </tbody>
        </table>
      </div>
    );
  }

  return (
    <div className={`${CARD_CLASS} ${fillHeight ? 'min-h-0 flex-1' : ''}`.trim()}>
      {toolbar && (
        <div className={`shrink-0 border-b px-4 py-4 ${ROW_BORDER_CLASS}`}>{toolbar}</div>
      )}
      {body()}
      {footer && (
        <div className={`shrink-0 border-t px-4 py-3 ${ROW_BORDER_CLASS}`}>{footer}</div>
      )}
    </div>
  );
}
